/**
 * Ядро списку будь-якої сутності (LST-01; components.md, розд. 7; макет list.html). Таблиця не знає
 * ні сутностей, ні звідки дані: колонки, комірки й відкриття запису дає `ListSource` (метадані
 * `/meta` — `platform/list-source.tsx`), сторінки — курсорний запит TanStack Query (`ListQuery`).
 * - `<table role="grid">`: фокус і `aria-selected` — на рядку, блукаючий tabindex; рядки даних
 *   з `aria-rowindex="2"`, `aria-rowcount` враховує шапку (−1 — загальна кількість невідома).
 * - У DOM лише видимі рядки + запас (≤ 2 500 вузлів, performance-budget.md, розд. 3); рядок у фокусі
 *   віртуалізатор не відмонтовує, навіть коли той за екраном (LST-14, WCAG 2.4.3). Висота рядка —
 *   `rowHeight` профілю можливостей, що збігається з --cl-row-h.
 * - Курсорна пагінація (LST-14) підвантажує при прокручуванні; в кінці — 3 рядки-скелетони,
 *   запасна кнопка «Показати ще», рядок «Не вдалося завантажити далі · Ще раз» і межа 5 000.
 * - Клавіші (LST-36; keyboard.md, розд. 2): J/K ↑/↓ (на останньому — далі), Home/End, PgUp/PgDn,
 *   Enter, Ctrl+Enter — у новій вкладці, Space, Shift+J/K — розширити вибір, Ctrl+A, C, L, Esc.
 * - Миша (LST-19): назва — справжнє посилання; клік по рядку лише ставить фокус, подвійний — відкриває.
 * - Ядро лишається легким (перший екран, performance-budget.md, розд. 3): сортування в шапці,
 *   вибір стовпців і живе оновлення — окремими модулями через `import()` (E4a-W2). Ядро дає їм
 *   лише слоти: вміст шапки (`header`), позначки рядків (`marks`), скидання на початок (`resetKey`).
 * - Чекбокс і назва закріплені ліворуч, коли таблиця ширша за область (LST-04).
 */
import {
  type InfiniteData,
  type QueryKey,
  type UseInfiniteQueryOptions,
  useInfiniteQuery,
} from '@tanstack/react-query';
import {
  createContext,
  type KeyboardEvent,
  type MouseEvent,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { focusKeyAllowed, focusShiftKeyAllowed, useHotkeys } from '../../app/hotkeys.ts';
import { LIST_KEY_HINTS } from '../../app/keymap.ts';
import { useNow } from '../../app/now.ts';
import { readScreen } from '../../app/return-state.ts';
import { useScreenReturn } from '../../app/use-screen-return.ts';
import { formatNumber as countLabel, useRegionKey } from '../../i18n/format.ts';
import { common as tc } from '../../i18n/uk/common.ts';
import { list as t } from '../../i18n/uk/list.ts';
import { markOnceAfterPaint, PERF_MARKS } from '../../perf/marks.ts';
import { useCapabilities, usePrefs } from '../../prefs/prefs.ts';
import { estimateContentWidth } from '../../shell/geometry.ts';
import { toast } from '../../ui/notify/store.ts';
import { Pending } from '../../ui/Pending.tsx';
import { SkeletonTable } from '../../ui/Skeleton.tsx';
import { errorCode, StateView } from '../../ui/StateView.tsx';
import { CHECK_W, type ColumnSpec, minTableWidth, useFittedColumns } from './columns.ts';
import { ROW_INTENT_MS, rowIntentClock } from './row-intent.ts';
import { useRowWindow } from './row-window.ts';

/** Рядок списку: таблиці потрібен лише id запису (ключ рядка, вибір, фокус). */
export interface ListRow {
  id: string;
}

/** Сторінка курсорного списку (контракт `recordPageSchema`). */
export interface ListPage<R extends ListRow> {
  items: R[];
  nextCursor: string | null;
  /** Скільки всього рядків у поданні; API платформи її не дає (views.md, розд. 7). */
  total?: number;
}

/** Запит сторінок списку (`infiniteQueryOptions`): курсор — параметр сторінки. */
export type ListQuery<
  P extends ListPage<ListRow>,
  K extends QueryKey = QueryKey,
> = UseInfiniteQueryOptions<P, Error, InfiniteData<P>, K, string>;

/** Напрям сортування колонки для `aria-sort`; `undefined` — колонку не відсортовано. */
export type SortState = 'ascending' | 'descending' | undefined;

/**
 * Позначка рядка живого оновлення (LST-18): `updated` — щойно змінено колегою (2 с фону),
 * `removed` — видалено, `gone` — більше недоступний. Рядок лишається на місці до оновлення.
 */
export type RowMark = 'updated' | 'removed' | 'gone';

/** Запит упав за межею часу (LST-16, `422 query_timeout`): «подання надто важке». */
export const isQueryTimeout = (error: unknown): boolean =>
  (error as { code?: unknown } | null | undefined)?.code === 'query_timeout';

/** Що відкрити в картці одразу: `call` — після «Подзвонити» з рядка, `log` — після `L`. */
export type OpenMode = 'call' | 'log';

/** Звідки список бере вигляд і поведінку рядків: метадані й маршрути, а не код сутності. */
export interface ListSource<R extends ListRow> {
  /** Назва подання: підпис таблиці для читача екрана й скелетона. */
  label: string;
  /** Колонки подання в його порядку; які з них вміщаються, вирішує таблиця. */
  columns: readonly ColumnSpec[];
  /** Сортування подання — для `aria-sort` у шапці. */
  sort?: { field: string; dir: 'asc' | 'desc' } | undefined;
  /** Назва запису: підпис чекбокса. */
  rowLabel: (row: R) => string;
  /** Адреса картки: посилання в назві й `Ctrl+Enter`. */
  href: (row: R) => string;
  /** Відкрити картку в цій вкладці (з контекстом списку); ставить мітку `cardOpenStart`. */
  open: (row: R, mode?: OpenMode) => void;
  /** Вміст комірки за типом поля; назву (`isTitle`) таблиця загортає в посилання сама. */
  cell: (column: ColumnSpec, row: R, ctx: CellContext<R>) => ReactNode;
  /** Повне значення в підказці, коли текст обрізано трикрапкою (LST-06). */
  tooltip?: ((column: ColumnSpec, row: R) => string | undefined) | undefined;
  /** Підготувати картку наперед при наведенні (лише поза легким режимом, LST-19). */
  preload?: ((row: R) => void) | undefined;
  /** Чи має запис форму результату (`L`). */
  canLog?: ((row: R) => boolean) | undefined;
}

/** Що комірка може взяти від таблиці: «зараз» (відносні дати) і дзвінок з рядка. */
export interface CellContext<R extends ListRow> {
  now: number;
  /** Дзвінок з рядка: картка одразу з панеллю результату (J-03). */
  call: (row: R) => void;
}

/** Пам'ять повернення до екрана (UX-08): ключ і текст оголошення «рядок N з M». */
export interface ScreenReturn {
  key: string;
  announce: (n: number, total: number) => string;
}

/**
 * Межа завантажених рядків на подання (LST-14): пам'ять вкладки ≤ 60 МБ, SEC-072 (масове
 * читання). Далі — банер «уточніть фільтр»; межу можна буде змінити налаштуванням.
 */
export const MAX_LOADED_ROWS = 5_000;

/**
 * Вибір для дій панелі (LST-24, LST-25): явні id завантажених рядків або «усі за фільтром» без
 * знятих вручну (`excludeIds`). `count` — скільки обрано, коли загальна кількість відома.
 */
export type TableSelection =
  | { kind: 'ids'; ids: readonly string[] }
  | { kind: 'query'; excludeIds: readonly string[]; count: number | null };

/** Вибір рядків — через контекст: його читає лише чекбокс, інші комірки — пропсами. */
interface RowApi {
  /** Роль має масові дії (LST-24): інакше чекбоксів немає. */
  selectable: boolean;
  isSelected: (id: string) => boolean;
  toggle: (id: string, index: number, range: boolean) => void;
}
const RowApiContext = createContext<RowApi | null>(null);
const useRowApi = () => useContext(RowApiContext) as RowApi;

function SelectCell({ label, id, index }: { label: string; id: string; index: number }) {
  const api = useRowApi();
  if (!api.selectable) return null;
  return (
    <input
      type="checkbox"
      aria-label={`${t.selectRow} «${label}»`}
      checked={api.isSelected(id)}
      tabIndex={-1}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) =>
        api.toggle(id, index, (e.nativeEvent as unknown as globalThis.MouseEvent).shiftKey)
      }
    />
  );
}

/** Запас рядків над і під видимими: менше DOM на першому рендері; у легкому режимі — ще менше. */
const OVERSCAN = { normal: 4, lite: 2 } as const;
const SKELETON_ROWS = 3;
const PREFETCH_ROWS = 16;
const PREFETCH_ROWS_LITE = 6;
/** Верхня панель + панель екрана (tokens.css: --cl-topbar-h + --cl-toolbar-h). */
const CHROME_H = { comfortable: 96, compact: 80 } as const;

/** Клік з модифікатором чи не лівою кнопкою — справа браузера (нова вкладка, вікно). */
const modifiedClick = (e: MouseEvent) =>
  e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0;

/** Клік по елементу з власною дією (дзвінок, посилання, кнопка, чекбокс) — не дія рядка. */
const ownAction = (target: HTMLElement) =>
  target.closest('[data-call],a,button,input,select,textarea') !== null;

interface RecordTableProps<R extends ListRow, P extends ListPage<R>, K extends QueryKey> {
  source: ListSource<R>;
  query: ListQuery<P, K>;
  /** Порожнє подання (LST-35): тексти й дія — від екрана. */
  empty: ReactNode;
  /** Помилка першої сторінки; без пропа — загальний стан помилки з кодом і повтором. */
  error?: ((error: unknown, retry: () => void) => ReactNode) | undefined;
  /** Повернення до списку на те саме місце й рядок (UX-08). */
  screen?: ScreenReturn | undefined;
  /** Оцінка ширини області до першого виміру, px (shell/geometry.ts); далі — ResizeObserver. */
  widthHint?: number | undefined;
  /** `data-testid` таблиці для e2e (за замовчуванням — `platform-list`). */
  testId?: string | undefined;
  /**
   * Вміст комірки шапки замість підпису: сортування в шапці дає лінивий модуль (LST-10), до його
   * завантаження — просто підпис. `aria-sort` ставить таблиця.
   */
  header?: ((column: ColumnSpec, sort: SortState) => ReactNode) | undefined;
  /** Позначки рядків за id (живе оновлення, LST-18). */
  marks?: ReadonlyMap<string, RowMark> | undefined;
  /** Новий ключ (змінилось сортування) — прокрутка й фокус на початок списку (LST-10). */
  resetKey?: string | undefined;
  /** Точна кількість записів подання (лічильник, LST-15) — для `aria-rowcount`. */
  total?: number | undefined;
  /**
   * Людина навела курсор (`pointer`) чи поставила фокус на таблицю: екран може підвантажити ліниві
   * модулі. Наведення в легкому режимі екран ігнорує (lite-mode.md: без завантаження на наведення).
   */
  onIntent?: ((kind: 'pointer' | 'focus') => void) | undefined;
  /**
   * Клік по шапці, поки модуля сортування ще немає (`header` не задано): екран вантажить модуль і
   * застосовує цей клік, щойно той прийде.
   */
  onHeaderPress?: ((column: ColumnSpec) => void) | undefined;
  /**
   * Дії панелі вибору від екрана, праворуч від «Зняти вибір» (trash.html, кадр Б1): таблиця не знає,
   * що можна робити з записами. `clear` — зняти вибір після дії.
   */
  selectionActions?: ((selection: TableSelection, clear: () => void) => ReactNode) | undefined;
  /** Чекбокси й вибір (LST-24): немає, коли роль не має жодної дії над обраними. */
  selectable?: boolean | undefined;
  /**
   * Відбиток набору записів (фільтр, подання): його зміна знімає вибір з тостом (LST-24), бо обрані
   * записи могли випасти з набору. Сортування сюди не входить — набір той самий.
   */
  selectionScope?: string | undefined;
  /**
   * Найбільше явних id, які приймають дії екрана: обрано більше — пропонуємо «усі за фільтром»,
   * навіть коли все завантажено, бо інакше дії неактивні без виходу (рев'ю QA #1023, S2).
   */
  selectionLimit?: number | undefined;
}

export function RecordTable<R extends ListRow, P extends ListPage<R>, K extends QueryKey>({
  source,
  query: options,
  empty,
  error,
  screen,
  widthHint,
  testId = 'platform-list',
  header,
  marks,
  resetKey,
  total: knownTotal,
  onIntent,
  onHeaderPress,
  selectionActions,
  selectable = true,
  selectionScope = '',
  selectionLimit,
}: RecordTableProps<R, P, K>) {
  const region = useRegionKey();
  const caps = useCapabilities();
  const { density } = usePrefs();
  const now = useNow();
  const query = useInfiniteQuery(options);
  const rows = useMemo<R[]>(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data]);
  const total = knownTotal ?? query.data?.pages[0]?.total;
  const capped = rows.length >= MAX_LOADED_ROWS;
  const canLoadMore = query.hasNextPage && !capped;
  const nextFailed = query.isFetchNextPageError;
  const scrollRef = useRef<HTMLDivElement>(null);
  // Колонки, що вміщаються в ширину області (columns.ts): пріоритет — з подання в метаданих.
  const visibleColumns = useFittedColumns(
    source.columns,
    scrollRef,
    rows.length > 0,
    widthHint ?? estimateContentWidth(false),
  );
  const [focused, setFocused] = useState(0);
  // Вибір (LST-24, LST-25): `selected` — обрані id, а в режимі «усі за фільтром» (`allMatching`) —
  // зняті вручну винятки; запит, а не список id, тож браузер не тримає тисяч id.
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const lastToggled = useRef<number | null>(null);
  const clearSelection = useCallback(() => {
    setSelected(new Set());
    setAllMatching(false);
  }, []);
  const selectLoaded = () => {
    setAllMatching(false);
    setSelected(new Set(rows.map((r) => r.id)));
  };
  /** Позначити рядок обраним чи ні з урахуванням режиму винятків. */
  const mark = (set: Set<string>, id: string, on: boolean) => {
    if (on !== allMatching) set.add(id);
    else set.delete(id);
  };
  const isSelected = (id: string) => selected.has(id) !== allMatching;
  // Змінився набір (фільтр, подання): вибір знято з тостом (LST-24, критерій 3). Чи був вибір —
  // з останнього рендеру через ref: функція оновлення `setState` може виконатись уже після
  // мікрозадачі тосту (#1027, S3; pitfalls/backend-and-web.md, «функція оновлення читає ref»).
  const lastScope = useRef(selectionScope);
  const hadSelection = useRef(false);
  hadSelection.current = selected.size > 0 || allMatching;
  useEffect(() => {
    if (lastScope.current === selectionScope) return;
    lastScope.current = selectionScope;
    if (!hadSelection.current) return;
    setSelected(new Set());
    setAllMatching(false);
    toast({ text: t.selectionReset });
  }, [selectionScope]);
  const [intentClock] = useState(rowIntentClock);
  const cancelPrefetch = useRef<() => void>(() => {});
  useEffect(() => () => cancelPrefetch.current(), []);
  /** Рядок, на який людина йшла клавішею за межу завантаженого: фокус — щойно він прийде. */
  const pendingFocus = useRef<number | null>(null);
  // Повернення до списку (UX-08): перший кадр — одразу на збереженому місці, без стрибка з верху.
  const [initialOffset] = useState(() => (screen ? (readScreen(screen.key)?.scroll ?? 0) : 0));

  const count = rows.length + (canLoadMore && !nextFailed ? SKELETON_ROWS : 0);
  // Рядок у фокусі лишається в DOM, навіть коли прокрутили далеко (LST-14): інакше фокус клавіатури
  // впаде на body (`keep`). Висота рядка стала — віртуалізація рядків сталої висоти (row-window.ts).
  const rowWindow = useRowWindow({
    count,
    rowHeight: caps.rowHeight,
    overscan: caps.lite ? OVERSCAN.lite : OVERSCAN.normal,
    // Перший кадр малює рядки одразу, не чекаючи виміру контейнера (LCP): висота — вікно мінус
    // оболонка; window.innerHeight розкладки не потребує.
    initialHeight: Math.max(caps.rowHeight * 4, window.innerHeight - CHROME_H[density]),
    initialOffset,
    scrollRef,
    keep: focused,
    getItemKey: (i) => rows[i]?.id ?? `sk-${i}`,
  });
  const { items, endIndex: visibleEnd, totalSize, scrollOffset, viewportHeight } = rowWindow;
  const { scrollToIndex, scrollToOffset } = rowWindow;

  // Змінилось сортування (LST-10): список з початку, фокус — на першому рядку. Вибір лишається: набір
  // записів той самий. Поки йде нова перша порція, видно старі рядки (`placeholderData`).
  const lastReset = useRef(resetKey);
  useEffect(() => {
    if (lastReset.current === resetKey) return;
    lastReset.current = resetKey;
    setFocused(0);
    scrollToOffset(0);
  }, [resetKey, scrollToOffset]);

  // Наступну сторінку тягнемо, коли під видимою частиною лишається ≤ prefetchRows завантажених
  // рядків (легкий режим — менше, щоб друга сторінка не йшла одразу на старті: бюджет ≤ 10
  // запитів). Без читання DOM: воно змусило б браузер синхронно рахувати розкладку. Висота —
  // з ResizeObserver віртуалізатора (до нього — initialHeight), прокрутка — його ж, шапка — рядок.
  // Після збою підвантаження — лише за кнопкою «Ще раз»: інакше прокрутка повторювала б запит.
  const prefetchRows = caps.lite ? PREFETCH_ROWS_LITE : PREFETCH_ROWS;
  // biome-ignore lint/correctness/useExhaustiveDependencies: visibleEnd — тригер перевірки на прокрутці
  useEffect(() => {
    if (!canLoadMore || nextFailed || query.isFetchingNextPage) return;
    const height = viewportHeight();
    const seenRows = (scrollOffset() + height) / caps.rowHeight - 1;
    if (seenRows >= rows.length - prefetchRows) void query.fetchNextPage();
  }, [
    visibleEnd,
    rows.length,
    query,
    caps.rowHeight,
    prefetchRows,
    scrollOffset,
    viewportHeight,
    canLoadMore,
  ]);

  useEffect(() => {
    if (rows.length > 0) markOnceAfterPaint(PERF_MARKS.listReady);
  }, [rows.length]);

  // Мітку `cardOpenStart` (заміри відкриття картки) ставить опис списку: дзвінок з комірки йде повз таблицю.
  const open = source.open;

  const api = useMemo<RowApi>(
    () => ({
      selectable,
      isSelected: (id) => selected.has(id) !== allMatching,
      toggle: (id, index, range) => {
        // Якір — до `setSelected`: оновлення React відкладене й бачило б уже новий якір.
        const from = range && lastToggled.current !== null ? lastToggled.current : index;
        setSelected((prev) => {
          const next = new Set(prev);
          const on = prev.has(id) === allMatching;
          const [a, b] = from < index ? [from, index] : [index, from];
          for (let i = a; i <= b; i++) {
            const rowId = rows[i]?.id;
            if (!rowId) continue;
            if (on !== allMatching) next.add(rowId);
            else next.delete(rowId);
          }
          return next;
        });
        lastToggled.current = index;
      },
    }),
    [selected, rows, allMatching, selectable],
  );

  // Посилання tel: вже відкриває софтфон; картка одразу показує панель результату.
  const callRow = useCallback((row: R) => open(row, 'call'), [open]);

  // Фокус ставимо після коміту, у якому рядок уже є в DOM (`keep` його лишає), а не в
  // наступному кадрі: кадр може настати раніше за коміт, і фокус упав би на body. `focusTick` —
  // рендер і тоді, коли номер рядка той самий (фокус був деінде).
  const focusWanted = useRef<number | null>(null);
  const [focusTick, setFocusTick] = useState(0);
  const focusLoaded = useCallback(
    (index: number) => {
      const i = Math.max(0, Math.min(index, rows.length - 1));
      setFocused(i);
      focusWanted.current = i;
      setFocusTick((n) => n + 1);
      scrollToIndex(i);
      return i;
    },
    [rows.length, scrollToIndex],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: focusTick — запит фокуса з focusLoaded
  useEffect(() => {
    const i = focusWanted.current;
    if (i === null) return;
    const el = scrollRef.current?.querySelector<HTMLElement>(`tr[data-index="${i}"]`);
    if (!el) return;
    focusWanted.current = null;
    el.focus();
  }, [focusTick]);

  /** Фокус на рядок; за межею завантаженого — підвантажити й перейти, щойно прийде (LST-14). */
  const focusRow = useCallback(
    (index: number) => {
      if (index >= rows.length && canLoadMore) {
        pendingFocus.current = rows.length;
        if (!query.isFetchingNextPage) void query.fetchNextPage();
        return focusLoaded(rows.length - 1);
      }
      return focusLoaded(index);
    },
    [rows.length, canLoadMore, query, focusLoaded],
  );

  // Нова порція прийшла — фокус туди, куди людина йшла клавішею.
  useEffect(() => {
    const target = pendingFocus.current;
    if (target === null || target >= rows.length) return;
    pendingFocus.current = null;
    focusLoaded(target);
  }, [rows.length, focusLoaded]);

  // Рядків поменшало (видалено, вибули з подання після оновлення) — фокус на останньому наявному.
  useEffect(() => {
    if (rows.length > 0 && focused >= rows.length) setFocused(rows.length - 1);
  }, [rows.length, focused]);

  const ids = useMemo(() => (rows.length > 0 ? rows.map((r) => r.id) : null), [rows]);
  useScreenReturn({
    key: screen?.key ?? '',
    enabled: screen !== undefined,
    container: scrollRef,
    rows: ids,
    focusRow: (index) => {
      focusLoaded(index);
      return true;
    },
    announceText: (n, all) => screen?.announce(n, all) ?? '',
  });

  /** Розширити вибір на сусідній рядок і перейти на нього (Shift+J / Shift+K). */
  const extend = (dir: 1 | -1) => {
    const from = rows[focused];
    const to = focusRow(focused + dir);
    const next = rows[to];
    setSelected((prev) => {
      const set = new Set(prev);
      if (from) mark(set, from.id, true);
      if (next) mark(set, next.id, true);
      return set;
    });
    lastToggled.current = to;
  };

  // Клавіші таблиці обробляє onKeyDown нижче; тут — лише їхні підписи для шпаргалки `?`.
  useHotkeys(LIST_KEY_HINTS);
  const onKeyDown = (e: KeyboardEvent<HTMLTableElement>) => {
    // Шапка (сортування, вибір усіх) обробляє свої клавіші сама: Enter там не відкриває рядок.
    if (e.target instanceof HTMLInputElement || (e.target as Element).closest('thead')) return;
    const row = rows[focused];
    const native = e.nativeEvent;
    const mod = (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey;
    // Ctrl / ⌘ + Enter — у новій вкладці; Ctrl / ⌘ + A — усі завантажені (LST-36).
    if (mod && !native.repeat && (e.code === 'Enter' || e.code === 'KeyA')) {
      e.preventDefault();
      if (e.code === 'KeyA') {
        if (selectable) selectLoaded();
      } else if (row) window.open(source.href(row), '_blank', 'noopener');
      return;
    }
    if (focusShiftKeyAllowed(native)) {
      const dir = { KeyJ: 1, ArrowDown: 1, KeyK: -1, ArrowUp: -1 }[e.code] as 1 | -1 | undefined;
      if (dir && selectable) {
        e.preventDefault();
        extend(dir);
      } else if (e.code === 'Space' && selectable && row) {
        // Shift+Space — діапазон від останнього обраного до рядка у фокусі (LST-24, критерій 1).
        e.preventDefault();
        api.toggle(row.id, focused, true);
      }
      return;
    }
    if (!focusKeyAllowed(native)) return;
    const page = Math.max(
      1,
      Math.floor((scrollRef.current?.clientHeight ?? 400) / caps.rowHeight) - 1,
    );
    const actions: Record<string, () => void> = {
      ArrowDown: () => focusRow(focused + 1),
      KeyJ: () => focusRow(focused + 1),
      ArrowUp: () => focusRow(focused - 1),
      KeyK: () => focusRow(focused - 1),
      Home: () => focusRow(0),
      // На останньому завантаженому — наступна порція (LST-14).
      End: () => focusRow(focused === rows.length - 1 ? rows.length : rows.length - 1),
      PageDown: () => focusRow(focused + page),
      PageUp: () => focusRow(focused - page),
      Enter: () => row && open(row),
      Space: () => selectable && row && api.toggle(row.id, focused, false),
      KeyC: () =>
        scrollRef.current
          ?.querySelector<HTMLAnchorElement>(`tr[data-index="${focused}"] [data-call]`)
          ?.click(),
      KeyL: () => row && source.canLog?.(row) && open(row, 'log'),
      Escape: clearSelection,
    };
    const action = actions[e.code];
    if (!action) return;
    e.preventDefault();
    action();
  };

  const onRowClick = useCallback(
    (e: MouseEvent<HTMLTableRowElement>, row: R, index: number) => {
      setFocused(index);
      const target = e.target as HTMLElement;
      // Назва — справжнє посилання: Ctrl/⌘/Shift-клік і середня кнопка — браузером (нова вкладка),
      // звичайний клік — перехід без перезавантаження. Клік деінде в рядку лише ставить фокус:
      // людина могла виділяти текст чи цілитись у клітинку (LST-19).
      if (target.closest('a[data-title]') && !modifiedClick(e)) {
        e.preventDefault();
        open(row);
      }
    },
    [open],
  );

  const onRowDoubleClick = useCallback(
    (e: MouseEvent<HTMLTableRowElement>, row: R) => {
      if (ownAction(e.target as HTMLElement)) return;
      open(row);
    },
    [open],
  );

  const preload = source.preload;
  const onRowEnter = useCallback(
    (row: R) => {
      if (!caps.prefetch || !preload) return;
      cancelPrefetch.current();
      cancelPrefetch.current = intentClock.after(ROW_INTENT_MS, () => preload(row));
    },
    [caps.prefetch, preload, intentClock],
  );

  if (query.isPending) {
    return (
      <div className="content" aria-busy="true">
        <Pending
          skeleton={<SkeletonTable label={source.label} rows={12} />}
          onRetry={() => void query.refetch()}
        />
      </div>
    );
  }
  if (query.isError && rows.length === 0) {
    const retry = () => void query.refetch();
    return (
      <div className="content">
        {error ? (
          error(query.error, retry)
        ) : (
          <StateView
            icon="alert-circle"
            tone="error"
            title={t.errorListTitle}
            text={tc.errorText}
            code={errorCode(query.error)}
          >
            <button className="btn btn--primary" type="button" onClick={retry}>
              {tc.retry}
            </button>
          </StateView>
        )}
      </div>
    );
  }
  if (rows.length === 0) return <div className="content">{empty}</div>;

  const headers = visibleColumns.length + 1;
  const allLoadedSelected = allMatching
    ? selected.size === 0
    : selected.size > 0 && selected.size === rows.length;
  // Обрано всі завантажені, а за фільтром їх більше: крок 2 шаблону Gmail (LST-25). Або обрано
  // більше, ніж приймають дії явними id: вихід — той самий вибір за фільтром.
  const moreMatching = total !== undefined ? total > rows.length : query.hasNextPage;
  const overLimit = selectionLimit !== undefined && selected.size > selectionLimit;
  const offerAll =
    selectable &&
    !allMatching &&
    ((allLoadedSelected && moreMatching) || overLimit) &&
    selectionActions !== undefined;
  const selectedCount = allMatching
    ? total !== undefined
      ? Math.max(0, total - selected.size)
      : null
    : selected.size;
  const selection: TableSelection = allMatching
    ? { kind: 'query', excludeIds: [...selected], count: selectedCount }
    : { kind: 'ids', ids: [...selected] };
  const hasSelection = allMatching || selected.size > 0;
  const sortOf = (key: string) =>
    key === source.sort?.field
      ? source.sort.dir === 'asc'
        ? 'ascending'
        : 'descending'
      : undefined;
  const rowCount = total !== undefined ? total + 1 : canLoadMore ? -1 : rows.length + 1;

  // Рядки й розпірки між ними: діапазон може мати розрив — рядок у фокусі далеко від видимих.
  const body: ReactNode[] = [];
  let offset = 0;
  for (const item of items) {
    // Верхня розпірка — зі сталим ключем: інакше на кожен кадр прокрутки новий вузол DOM.
    if (item.start > offset)
      body.push(
        <Spacer
          key={offset === 0 ? 'gap-start' : `gap-${item.key}`}
          height={item.start - offset}
          cols={headers}
        />,
      );
    offset = item.end;
    const row = rows[item.index];
    if (!row) {
      body.push(<SkeletonRow key={item.key} cols={headers} />);
      continue;
    }
    body.push(
      <RecordTr
        key={item.key}
        index={item.index}
        row={row}
        focused={item.index === focused}
        selected={isSelected(row.id)}
        mark={marks?.get(row.id)}
        now={now}
        region={region}
        source={source}
        columns={visibleColumns}
        onClick={onRowClick}
        onDoubleClick={onRowDoubleClick}
        onEnter={onRowEnter}
        onCall={callRow}
      />,
    );
  }
  const tail = totalSize - offset;
  if (tail > 0) body.push(<Spacer key="gap-end" height={tail} cols={headers} />);

  return (
    <RowApiContext.Provider value={api}>
      <div className="content vlist" ref={scrollRef} data-testid="list-scroll">
        {/* Опора висоти (тікет 79b9a9ec): коміт прокрутки спершу видаляє рядки й розпірку, що
            пішли вгору, а нові вставляє потім. WebKit (Safari 26) інколи рахує розкладку між цими
            кроками: таблиця на мить коротша, і браузер урізає scrollTop до нового максимуму, а
            віртуалізатор малює вже з урізаної позиції — список відкидає назад по колу. Опора
            тримає прокручувану висоту не меншою за шапку + усі рядки, тож урізати нема чого. */}
        <div
          className="vlist__extent"
          aria-hidden="true"
          style={{ height: caps.rowHeight + totalSize }}
        />
        <table
          className="table"
          // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: таблиця-grid з фокусом на рядку (components.md, розд. 7)
          role="grid"
          aria-label={source.label}
          aria-rowcount={rowCount}
          aria-multiselectable="true"
          aria-busy={query.isFetchingNextPage || query.isPlaceholderData || undefined}
          data-stale={query.isPlaceholderData || undefined}
          data-testid={testId}
          onKeyDown={onKeyDown}
          onPointerOver={onIntent && (() => onIntent('pointer'))}
          onFocus={onIntent && (() => onIntent('focus'))}
          onClick={
            header || !onHeaderPress
              ? undefined
              : (e) => {
                  const key = (e.target as Element)
                    .closest('th[data-col]')
                    ?.getAttribute('data-col');
                  const column = visibleColumns.find((c) => c.key === key);
                  if (column) onHeaderPress(column);
                }
          }
          // Пріоритет 1 не вміщається навіть сам — горизонтальна прокрутка в межах списку.
          style={{ minWidth: minTableWidth(visibleColumns) }}
        >
          <colgroup>
            <col className="col-check" style={{ width: CHECK_W }} />
            {visibleColumns.map((c) => (
              <col
                key={c.key}
                className={c.className}
                // Ручка ширини (LST-03) тягне саме цей `<col>`, без рендеру на кожен рух.
                data-col={c.key}
                style={c.width === null ? undefined : { width: c.width }}
              />
            ))}
          </colgroup>
          <thead>
            <tr aria-rowindex={1}>
              <th scope="col" className="col-check">
                {selectable ? (
                  <input
                    type="checkbox"
                    aria-label={t.selectAll}
                    checked={allLoadedSelected}
                    ref={(el) => {
                      // «Частково» (LST-24): обрано не всі завантажені.
                      if (el) el.indeterminate = hasSelection && !allLoadedSelected;
                    }}
                    onChange={() => (allLoadedSelected ? clearSelection() : selectLoaded())}
                  />
                ) : (
                  // Без прав на дії чекбоксів немає, а заголовок стовпця не може бути порожнім
                  // (axe `empty-table-header`): назва — лише для програми читання з екрана.
                  <span className="sr-only">{t.selectColumn}</span>
                )}
              </th>
              {visibleColumns.map((c, i) => {
                const sort = sortOf(c.key);
                return (
                  <th
                    key={c.key}
                    scope="col"
                    className={cellClass(c, i)}
                    aria-sort={sort}
                    data-col={c.key}
                    // Повна назва, коли її обрізано трикрапкою (a11y-i18n.md, розд. 6); з модулем
                    // сортування підказку дає кнопка.
                    title={header ? undefined : c.label}
                  >
                    {header ? header(c, sort) : <HeaderLabel label={c.label} sort={sort} />}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>{body}</tbody>
        </table>
        <ListTail
          capped={capped && query.hasNextPage}
          failed={nextFailed}
          heavy={nextFailed && isQueryTimeout(query.error)}
          more={canLoadMore && !nextFailed && !query.isFetchingNextPage}
          pageSize={caps.pageSize}
          onMore={() => void query.fetchNextPage()}
        />
        {hasSelection && (
          <section className="bulkbar" aria-label={t.selected} data-testid="bulkbar">
            <span aria-live="polite">
              {t.selected}:{' '}
              <strong data-testid="bulk-count">
                {selectedCount === null ? t.allMatchingUnknown : countLabel(selectedCount)}
              </strong>
            </span>
            {offerAll && (
              <button
                className="btn btn--sm btn--ghost"
                type="button"
                data-testid="bulk-select-all"
                onClick={() => {
                  setAllMatching(true);
                  setSelected(new Set());
                }}
              >
                {total !== undefined
                  ? t.selectAllMatching(countLabel(total))
                  : t.selectAllMatchingUnknown}
              </button>
            )}
            <button className="btn btn--sm" type="button" onClick={clearSelection}>
              {t.clearSelection} <kbd>Esc</kbd>
            </button>
            {selectionActions?.(selection, clearSelection)}
          </section>
        )}
      </div>
    </RowApiContext.Provider>
  );
}

/**
 * Кінець завантаженого (LST-14, LST-35): запасна «Показати ще» (автопідвантаження не спрацювало,
 * читач екрана), «Не вдалося завантажити далі · Ще раз» і банер межі 5 000.
 */
function ListTail({
  capped,
  failed,
  heavy,
  more,
  pageSize,
  onMore,
}: {
  capped: boolean;
  failed: boolean;
  /** Наступна порція впала за межею часу (LST-16). */
  heavy: boolean;
  more: boolean;
  pageSize: number;
  onMore: () => void;
}) {
  if (capped)
    return (
      <p className="list-tail" role="status" data-testid="list-capped">
        {t.listCapped(MAX_LOADED_ROWS)}
      </p>
    );
  if (failed)
    return (
      <p className="list-tail list-tail--error" role="alert" data-testid="list-more-failed">
        {heavy ? t.loadMoreTooHeavy : t.loadMoreFailed}
        <button className="btn btn--sm" type="button" onClick={onMore}>
          {tc.retry}
        </button>
      </p>
    );
  if (!more) return null;
  return (
    <p className="list-tail">
      <button className="btn btn--sm" type="button" onClick={onMore} data-testid="list-more">
        {t.loadMore(pageSize)}
      </button>
    </p>
  );
}

/** Підпис шапки без модуля сортування; стрілка — напрям сортування подання. */
function HeaderLabel({ label, sort }: { label: string; sort: SortState }) {
  return (
    <>
      {label}
      {sort && (
        <i
          className={`ic ic-sort-down${sort === 'ascending' ? ' ic--flip' : ''}`}
          aria-hidden="true"
        />
      )}
    </>
  );
}

/**
 * Клас комірки: назва першою колонкою закріплена разом із чекбоксом (LST-04). Якщо опис списку
 * поставив назву не першою, закріплення немає: інакше вона накрила б сусідні колонки.
 */
const cellClass = (column: ColumnSpec, index: number) =>
  column.isTitle && index === 0 ? `${column.className} is-pinned` : column.className;

interface RecordTrProps<R extends ListRow> {
  index: number;
  row: R;
  focused: boolean;
  selected: boolean;
  mark: RowMark | undefined;
  now: number;
  /** Мова й пояс (`useRegionKey`): без нього `memo` не перемалював би дати після зміни поясу. */
  region: string;
  source: ListSource<R>;
  columns: readonly ColumnSpec[];
  onClick: (e: MouseEvent<HTMLTableRowElement>, row: R, index: number) => void;
  onDoubleClick: (e: MouseEvent<HTMLTableRowElement>, row: R) => void;
  onEnter: (row: R) => void;
  onCall: (row: R) => void;
}

/**
 * Рядок перемальовується лише тоді, коли змінився хоч один його проп: дані, фокус, вибір, колонки
 * (INP на слабкому ПК). Комірки рендеримо тут, з `columns`, а `memo` — без власного компаратора:
 * доданий проп не загубиться в порівнянні. Колбеки батька й опис списку — стабільні.
 */
const RecordTr = memo(function RecordTr<R extends ListRow>({
  index,
  row,
  focused,
  selected,
  mark,
  now,
  region: _region,
  source,
  columns,
  onClick,
  onDoubleClick,
  onEnter,
  onCall,
}: RecordTrProps<R>) {
  const label = source.rowLabel(row);
  const ctx: CellContext<R> = { now, call: onCall };
  return (
    <tr
      data-index={index}
      data-id={row.id}
      aria-rowindex={index + 2}
      aria-selected={selected}
      tabIndex={focused ? 0 : -1}
      className={focused ? 'is-focused' : undefined}
      data-mark={mark}
      onClick={(e) => onClick(e, row, index)}
      onDoubleClick={(e) => onDoubleClick(e, row)}
      onMouseEnter={() => onEnter(row)}
    >
      <td className="col-check">
        <SelectCell label={label} id={row.id} index={index} />
      </td>
      {columns.map((column, i) => (
        <td
          key={column.key}
          className={cellClass(column, i)}
          title={column.isTitle ? undefined : source.tooltip?.(column, row) || undefined}
        >
          {column.isTitle ? (
            <>
              {/* Звичайне <a>, а не <Link> роутера: той у кожному рядку підписується на стан
                  роутера і будує адресу — помітна частина першого рендера на слабкому ПК. */}
              <a href={source.href(row)} tabIndex={-1} title={label} data-title>
                {source.cell(column, row, ctx) ?? label}
              </a>
              {(mark === 'removed' || mark === 'gone') && (
                <span className="row-mark">{mark === 'removed' ? t.rowRemoved : t.rowGone}</span>
              )}
            </>
          ) : (
            source.cell(column, row, ctx)
          )}
        </td>
      ))}
    </tr>
  );
}) as <R extends ListRow>(props: RecordTrProps<R>) => ReactNode;

function Spacer({ height, cols }: { height: number; cols: number }) {
  return (
    // biome-ignore lint/a11y/noAriaHiddenOnFocusable: рядок-розпірка віртуалізації не фокусується
    <tr className="vpad" aria-hidden="true">
      <td colSpan={cols} style={{ height }} />
    </tr>
  );
}

function SkeletonRow({ cols }: { cols: number }) {
  return (
    // biome-ignore lint/a11y/noAriaHiddenOnFocusable: рядок-скелетон не фокусується
    <tr className="sk-group" aria-hidden="true">
      <td colSpan={cols}>
        <span className="sk sk--line" />
      </td>
    </tr>
  );
}
